/**
 * AdminDashboardPage — CRM 2.0 Admin-Dashboard
 *
 * Aggregiert alle Karten aus dem System in einem konfigurierbaren Grid.
 * Jede Karte ist:
 *  - Verschiebbar + skalierbar via react-grid-layout
 *  - Aktivierbar/deaktivierbar per Settings-Panel
 *  - In zwei Modi darstellbar: "Übersicht" (summary) oder "ganzes Element" (full)
 *  - Der Titel verlinkt auf die jeweilige Vollliste (Punkt 2)
 *  - Hat einen +-Button für "Neuer Eintrag" (Punkt 3)
 *
 * Layout + Widget-Settings werden per User in dashboard_configs gespeichert.
 */
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridLayout, useContainerWidth, type Layout, type LayoutItem, type GridLayoutProps } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import { DENSITY_VALUES, getThemeColors } from '../../../lib/theme';
import { useIsMobile } from '../../../lib/useBreakpoint';
import { useDashboardConfig } from '../../../lib/useDashboardConfig';
import type { Density } from '../../../types';
import type { DashboardLayoutItem } from '../../../lib/api';
import { useDashboardData } from './useDashboardData';
import { WIDGETS, effectiveDashboardLayout } from './helpers';
import { DashboardToolbar } from './DashboardToolbar';
import { SettingsPanel } from './SettingsPanel';
import { WidgetCard } from './WidgetCard';
import { ErrorBanner } from './ErrorBanner';

interface AdminDashboardPageProps {
  dark: boolean;
  density: Density;
  setPage: (page: string) => void;
}

export function AdminDashboardPage({ dark, density, setPage }: AdminDashboardPageProps) {
  const t = getThemeColors(dark);
  const d = DENSITY_VALUES[density];
  const { t: tr } = useTranslation();
  const isMobile = useIsMobile();

  const {
    config, loading: configLoading, setLayout, setWidgetSettings, widgetSettings,
    source, canSetDefault, resetToDefault, saveAsDefault, clearDefault,
  } = useDashboardConfig(effectiveDashboardLayout);
  const areaControls = { source, canSetDefault, resetToDefault, saveAsDefault, clearDefault };
  const { width: gridWidth, containerRef: gridContainerRef, mounted: gridMounted } = useContainerWidth({ measureBeforeMount: true, initialWidth: 1240 });

  const { data, dataError } = useDashboardData();
  const [settingsOpen, setSettingsOpen] = useState(false);

  // Effective layout: stored config layout merged with defaults for any missing widget
  // (entfernte/unbekannte Widget-IDs aus alten Konfigurationen fallen heraus).
  const effectiveLayout: DashboardLayoutItem[] = useMemo(
    () => effectiveDashboardLayout(config.layout),
    [config.layout],
  );

  const handleLayoutChange = useCallback((newLayout: Layout) => {
    const items: DashboardLayoutItem[] = (newLayout as unknown as LayoutItem[]).map(l => ({
      i: l.i, x: l.x, y: l.y, w: l.w, h: l.h,
    }));
    setLayout(items);
  }, [setLayout]);

  const visibleWidgets = WIDGETS.filter(w => widgetSettings(w.id).visible);

  if (isMobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: d.gap }}>
        <DashboardToolbar
          dark={dark}
          settingsOpen={settingsOpen}
          onToggleSettings={() => setSettingsOpen(v => !v)}
        />
        {settingsOpen && (
          <SettingsPanel
            dark={dark}
            widgets={WIDGETS}
            widgetSettings={widgetSettings}
            setWidgetSettings={setWidgetSettings}
            areaControls={areaControls}
          />
        )}
        {dataError && <ErrorBanner dark={dark} message={dataError} />}
        {visibleWidgets.map(w => (
          <WidgetCard
            key={w.id}
            widget={w}
            mode={widgetSettings(w.id).mode}
            dark={dark}
            density={density}
            data={data}
            setPage={setPage}
            tr={tr}
          />
        ))}
      </div>
    );
  }

  // Desktop: react-grid-layout v2 API — width tracks the actual container
  // (ResizeObserver) instead of a fixed 1240px so the grid stays responsive
  // across window sizes / tablet widths.
  const gap = typeof d.gap === 'number' ? d.gap : 12;

  const rglLayout: LayoutItem[] = effectiveLayout
    .filter(l => widgetSettings(l.i).visible)
    .map(l => {
      const def = WIDGETS.find(w => w.id === l.i);
      return {
        i: l.i, x: l.x, y: l.y, w: l.w, h: l.h,
        minW: def?.minW ?? 2, minH: def?.minH ?? 2,
      };
    });

  const gridConfig: GridLayoutProps['gridConfig'] = {
    cols: 12,
    rowHeight: 60,
    margin: [gap, gap] as const,
    containerPadding: [0, 0] as const,
  };

  const dragConfig: GridLayoutProps['dragConfig'] = {
    enabled: true,
    handle: '.widget-drag-handle',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <DashboardToolbar
        dark={dark}
        settingsOpen={settingsOpen}
        onToggleSettings={() => setSettingsOpen(v => !v)}
      />

      {settingsOpen && (
        <SettingsPanel
          dark={dark}
          widgets={WIDGETS}
          widgetSettings={widgetSettings}
          setWidgetSettings={setWidgetSettings}
          areaControls={areaControls}
        />
      )}

      {dataError && <ErrorBanner dark={dark} message={dataError} />}

      <div ref={gridContainerRef as React.RefObject<HTMLDivElement>} style={{ width: '100%' }}>
      {(configLoading || !gridMounted) ? (
        <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>{tr('dashboard.page.loading_config')}</div>
      ) : (
        <GridLayout
          layout={rglLayout as unknown as Layout}
          width={gridWidth}
          gridConfig={gridConfig}
          dragConfig={dragConfig}
          onLayoutChange={handleLayoutChange}
          className="admin-dashboard-grid"
          style={{ minHeight: 400 }}
        >
          {visibleWidgets.map(w => (
            <div key={w.id} style={{ overflow: 'hidden' }}>
              <WidgetCard
                widget={w}
                mode={widgetSettings(w.id).mode}
                dark={dark}
                density={density}
                data={data}
                setPage={setPage}
                tr={tr}
                draggable
              />
            </div>
          ))}
        </GridLayout>
      )}
      </div>
    </div>
  );
}
